import { PlasticsColorCode } from './plastics-color-code.model';

export interface C3ClearanceInventoryCategory {
    prefix: string;
    name: string;
    salePrice: number | null;
    employeeSalePrice: number | null;
    cost: number | null;
    pricingNote: string;
    sortOrder: number;
}

export interface C3ClearanceInventoryItem {
    partNumber: string;
    description: string;
    quantity: number;
    inSystem: boolean;
    bodyColorCode: string;
    bodyColorName: string;
    bodyColorHex: string;
    note: string;
}

/**
 * Temporary clearance inventory stored as one global-variables document.
 * It intentionally does not reference C3 product or inventory records.
 */
export interface C3ClearanceInventory {
    categories: C3ClearanceInventoryCategory[];
    items: C3ClearanceInventoryItem[];
    lastUpdatedOn: Date;
    lastUpdatedById: string;
}

const CATEGORIES: C3ClearanceInventoryCategory[] = [
    {
        prefix: 'CC3000',
        name: 'Pickup',
        salePrice: null,
        employeeSalePrice: null,
        cost: null,
        pricingNote: '',
        sortOrder: 3000,
    },
    {
        prefix: 'CC4000',
        name: 'Rover',
        salePrice: null,
        employeeSalePrice: null,
        cost: null,
        pricingNote: '',
        sortOrder: 4000,
    },
    {
        prefix: 'CC4030',
        name: 'Cooler Trailer',
        salePrice: null,
        employeeSalePrice: null,
        cost: null,
        pricingNote: '',
        sortOrder: 4030,
    },
    {
        prefix: 'CC5000',
        name: 'Truck',
        salePrice: null,
        employeeSalePrice: null,
        cost: null,
        pricingNote: '',
        sortOrder: 5000,
    },
    {
        prefix: 'CC6000',
        name: 'Zamboni',
        salePrice: null,
        employeeSalePrice: null,
        cost: null,
        pricingNote: '',
        sortOrder: 6000,
    },
];

type C3ClearanceInventorySeedItem = Omit<
    C3ClearanceInventoryItem,
    'bodyColorCode' | 'bodyColorName' | 'bodyColorHex' | 'note'
>;

const ITEMS: C3ClearanceInventorySeedItem[] = [
    { partNumber: 'CC3000-073', description: 'RC PICK UP BODY-BROWN', quantity: 2, inSystem: true },
    { partNumber: 'CC3000-092', description: 'RC PICK UP BODY-BLACK', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-1235', description: 'RC PICK UP BODY-YELLOW', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-232', description: 'RC PICK UP BODY-WHITE', quantity: 2, inSystem: true },
    { partNumber: 'CC3000-268', description: 'RC PICK UP BODY-PURPLE', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-287', description: 'RC PICK UP BODY-BLUE', quantity: 6, inSystem: true },
    { partNumber: 'CC3000-289', description: 'BODY, PICKUP, NAVY', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-466', description: 'RC PICK UP BODY-TAN', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-5535', description: 'RC PICK UP BODY-GREEN', quantity: 1, inSystem: true },
    { partNumber: 'CC3000-806', description: 'RC PICK UP BODY-PINK', quantity: 2, inSystem: true },
    { partNumber: 'CC3000-3155', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC3000-5395', description: '', quantity: 1, inSystem: false },

    { partNumber: 'CC4000-063', description: 'BODY ROVER-GRAY', quantity: 4, inSystem: true },
    { partNumber: 'CC4000-073', description: 'BODY ROVER-BROWN', quantity: 13, inSystem: true },
    { partNumber: 'CC4000-082', description: 'BODY ROVER-GREEN', quantity: 3, inSystem: true },
    { partNumber: 'CC4000-092', description: 'ROVER BODY-BLACK', quantity: 1, inSystem: true },
    { partNumber: 'CC4000-1235', description: 'ROVER BODY-YELLOW', quantity: 9, inSystem: true },
    { partNumber: 'CC4000-130', description: 'BODY ROVER-ORANGE', quantity: 6, inSystem: true },
    { partNumber: 'CC4000-160', description: 'BODY ROVER-ORANGE', quantity: 2, inSystem: true },
    { partNumber: 'CC4000-186', description: 'BODY ROVER-RED', quantity: 10, inSystem: true },
    { partNumber: 'CC4000-195', description: 'BODY, ROVER, MAROON, MOLDED', quantity: 3, inSystem: true },
    { partNumber: 'CC4000-229', description: 'ROVER BODY-GREEN', quantity: 4, inSystem: true },
    { partNumber: 'CC4000-253', description: 'ROVER BODY-ORANGE', quantity: 4, inSystem: true },
    { partNumber: 'CC4000-264', description: 'ROVER BODY-YELLOW', quantity: 2, inSystem: true },
    { partNumber: 'CC4000-265', description: 'ROVER BODY-PROCESS BLUE', quantity: 1, inSystem: true },
    { partNumber: 'CC4000-273', description: 'BODY ROVER-INDIGO', quantity: 1, inSystem: true },
    { partNumber: 'CC4000-2758', description: 'BODY ROVER-BLUE', quantity: 3, inSystem: true },
    { partNumber: 'CC4000-2767', description: 'BODY ROVER-BLUE', quantity: 6, inSystem: true },
    { partNumber: 'CC4000-287', description: 'ROVER BODY-BLUE', quantity: 6, inSystem: true },
    { partNumber: 'CC4000-289', description: 'BODY ROVER-BLUE', quantity: 4, inSystem: true },
    { partNumber: 'CC4000-300', description: 'ROVER BODY-BLUE', quantity: 1, inSystem: true },
    { partNumber: 'CC4000-321', description: 'BODY ROVER-TEAL', quantity: 5, inSystem: true },
    { partNumber: 'CC4000-3308', description: 'ROVER BODY-GREEN', quantity: 3, inSystem: true },
    { partNumber: 'CC4000-3425', description: 'BODY, ROVER, GREEN, MOLDED', quantity: 2, inSystem: true },
    { partNumber: 'CC4000-466', description: 'BODY ROVER-TAN', quantity: 2, inSystem: true },
    { partNumber: 'CC4000-5395', description: 'BODY ROVER-BLUE', quantity: 17, inSystem: true },
    { partNumber: 'CC4000-654', description: 'BODY, ROVER, BLUE, MOLDED', quantity: 1, inSystem: true },
    { partNumber: 'CC4000-7462', description: 'BODY ROVER-BLUE', quantity: 2, inSystem: true },
    { partNumber: 'CC4000-7502', description: 'BODY ROVER-TAN', quantity: 1, inSystem: true },

    { partNumber: 'CC4030-092', description: 'BODY COOLER TRAILER-BLACK', quantity: 2, inSystem: true },
    { partNumber: 'CC4030-1235', description: 'BODY COOLER TRAILER-YELLOW', quantity: 1, inSystem: true },
    { partNumber: 'CC4030-187', description: 'BODY COOLER TRAILER-RED', quantity: 4, inSystem: true },
    { partNumber: 'CC4030-229', description: 'BODY, COOLER TRAILER, MOLDED, LIME GREEN', quantity: 2, inSystem: true },
    { partNumber: 'CC4030-268', description: 'BODY COOLER TRAILER-PURPLE', quantity: 1, inSystem: true },
    { partNumber: 'CC4030-287', description: 'BODY COOLER TRAILER-BLUE', quantity: 3, inSystem: true },
    { partNumber: 'CC4030-289', description: 'BODY COOLER TRAILER-NAVY', quantity: 2, inSystem: true },
    { partNumber: 'CC4030-130', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-195', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-265', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-273', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-300', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-316', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-321', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC4030-7502', description: '', quantity: 1, inSystem: false },

    { partNumber: 'CC5000-092', description: 'RC TRUCK BODY-BLACK', quantity: 3, inSystem: true },
    { partNumber: 'CC5000-130', description: 'RC TRUCK BODY', quantity: 2, inSystem: true },
    { partNumber: 'CC5000-187', description: 'RC TRUCK BODY', quantity: 14, inSystem: true },
    { partNumber: 'CC5000-195', description: 'RC TRUCK BODY-MAROON', quantity: 1, inSystem: true },
    { partNumber: 'CC5000-229', description: 'BODY, DELIVERY TRUCK, LIME GREEN, MOLDED', quantity: 3, inSystem: true },
    { partNumber: 'CC5000-232', description: 'RC TRUCK BODY-WHITE', quantity: 4, inSystem: true },
    { partNumber: 'CC5000-289', description: 'RC TRUCK BODY-BLUE', quantity: 6, inSystem: true },
    { partNumber: 'CC5000-268', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC5000-466', description: '', quantity: 1, inSystem: false },

    { partNumber: 'CC6000-092', description: 'RC ZAMBONI BODY-BLACK', quantity: 9, inSystem: true },
    { partNumber: 'CC6000-1235', description: 'RC ZAMBONI BODY-YELLOW', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-130', description: 'RC ZAMBONI BODY-ORANGE', quantity: 3, inSystem: true },
    { partNumber: 'CC6000-186', description: 'RC ZAMBONI BODY-RED', quantity: 4, inSystem: true },
    { partNumber: 'CC6000-187', description: 'RC ZAMBONI BODY-RED', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-195', description: 'RC ZAMBONI BODY-MAROON', quantity: 4, inSystem: true },
    { partNumber: 'CC6000-229', description: 'RC ZAMBONI BODY-GREEN', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-232', description: 'RC ZAMBONI BODY-WHITE', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-265', description: 'RC ZAMBONI BODY-PRO BLUE', quantity: 10, inSystem: true },
    { partNumber: 'CC6000-268', description: 'RC ZAMBONI BODY-PURPLE', quantity: 3, inSystem: true },
    { partNumber: 'CC6000-287', description: 'RC ZAMBONI BODY-BLUE', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-289', description: 'RC ZAMBONI BODY-BLUE', quantity: 1, inSystem: true },
    { partNumber: 'CC6000-321', description: 'RC ZAMBONI BODY-TEAL', quantity: 3, inSystem: true },
    { partNumber: 'CC6000-3425', description: 'RC ZAMBONI BODY-GREEN', quantity: 13, inSystem: true },
    { partNumber: 'CC6000-806', description: 'RC ZAMBONI BODY-PINK', quantity: 2, inSystem: true },
    { partNumber: 'CC6000-063', description: '', quantity: 1, inSystem: false },
    { partNumber: 'CC6000-273', description: '', quantity: 1, inSystem: false },
];

const GENERIC_COLOR_HEX: Record<string, string> = {
    Black: '#171717',
    Blue: '#2767B2',
    Brown: '#795548',
    Gray: '#808080',
    Green: '#3E8B57',
    Indigo: '#3F51B5',
    'Lime Green': '#7CB342',
    Maroon: '#7B263A',
    Navy: '#1D3557',
    Orange: '#E97824',
    Pink: '#E889B5',
    'Process Blue': '#0085CA',
    Purple: '#7851A9',
    Red: '#C73E3A',
    Tan: '#C2A878',
    Teal: '#168C8C',
    White: '#F4F4F0',
    Yellow: '#E4C62A',
};

const COLOR_NAME_MATCHERS: Array<{ pattern: RegExp; name: string }> = [
    { pattern: /LIME GREEN/, name: 'Lime Green' },
    { pattern: /PROCESS BLUE|PRO BLUE/, name: 'Process Blue' },
    { pattern: /NAVY/, name: 'Navy' },
    { pattern: /MAROON/, name: 'Maroon' },
    { pattern: /INDIGO/, name: 'Indigo' },
    { pattern: /PURPLE/, name: 'Purple' },
    { pattern: /ORANGE/, name: 'Orange' },
    { pattern: /YELLOW/, name: 'Yellow' },
    { pattern: /BROWN/, name: 'Brown' },
    { pattern: /WHITE/, name: 'White' },
    { pattern: /BLACK/, name: 'Black' },
    { pattern: /GRAY|GREY/, name: 'Gray' },
    { pattern: /GREEN/, name: 'Green' },
    { pattern: /BLUE/, name: 'Blue' },
    { pattern: /TEAL/, name: 'Teal' },
    { pattern: /TAN/, name: 'Tan' },
    { pattern: /PINK/, name: 'Pink' },
    { pattern: /RED/, name: 'Red' },
];

type ClearanceColorSource = Pick<PlasticsColorCode, 'code' | 'name' | 'hexValue'>;

function normalizeColorCode(code: string): string {
    return String(code ?? '').trim().replace(/^0+/, '') || '0';
}

function normalizedHex(hexValue: string | null | undefined): string | null {
    const normalized = String(hexValue ?? '').trim().toUpperCase();
    return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : null;
}

function partNumberColorCode(partNumber: string): string {
    const segments = partNumber.split('-');
    return segments[segments.length - 1] ?? '';
}

function fallbackColorName(code: string, items: C3ClearanceInventorySeedItem[]): string {
    const relatedDescriptions = items
        .filter((item) => partNumberColorCode(item.partNumber) === code)
        .map((item) => item.description.toUpperCase())
        .join(' ');
    return COLOR_NAME_MATCHERS.find((matcher) => matcher.pattern.test(relatedDescriptions))?.name
        ?? `Color ${code}`;
}

function clearanceColor(
    code: string,
    colorSources: ClearanceColorSource[],
    items: C3ClearanceInventorySeedItem[],
): { name: string; hexValue: string } {
    const source = colorSources.find((candidate) =>
        normalizeColorCode(candidate.code) === normalizeColorCode(code));
    const fallbackName = fallbackColorName(code, items);
    const name = source?.name?.trim() || fallbackName;
    const hexValue = normalizedHex(source?.hexValue)
        ?? GENERIC_COLOR_HEX[fallbackName]
        ?? '#777777';

    return { name, hexValue };
}

export function enrichC3ClearanceInventory(
    inventory: C3ClearanceInventory,
    colorSources: ClearanceColorSource[] = [],
): C3ClearanceInventory {
    const categories = inventory.categories.map((category) => ({
        prefix: category.prefix,
        name: category.name,
        salePrice: category.salePrice ?? null,
        employeeSalePrice: category.employeeSalePrice ?? null,
        cost: category.cost ?? null,
        pricingNote: category.pricingNote ?? '',
        sortOrder: category.sortOrder,
    }));

    const items = inventory.items.map((item) => {
        const bodyColorCode = item.bodyColorCode || partNumberColorCode(item.partNumber);
        const color = clearanceColor(bodyColorCode, colorSources, ITEMS);
        return {
            ...item,
            bodyColorCode,
            bodyColorName: item.bodyColorName || color.name,
            bodyColorHex: normalizedHex(item.bodyColorHex) ?? color.hexValue,
            note: item.note ?? '',
        };
    });

    return {
        categories,
        items,
        lastUpdatedOn: new Date(inventory.lastUpdatedOn),
        lastUpdatedById: inventory.lastUpdatedById,
    };
}

export function createInitialC3ClearanceInventory(
    userId: string,
    colorSources: ClearanceColorSource[] = [],
): C3ClearanceInventory {
    return enrichC3ClearanceInventory({
        categories: CATEGORIES.map((category) => ({ ...category })),
        items: ITEMS.map((item) => ({
            ...item,
            bodyColorCode: '',
            bodyColorName: '',
            bodyColorHex: '',
            note: '',
        })),
        lastUpdatedOn: new Date(),
        lastUpdatedById: userId,
    }, colorSources);
}
